import { useEffect, useMemo, useState } from 'react';
import './App.css';
import './index.css';
import { Outlet, useNavigate } from 'react-router-dom';
import { AppProvider } from '@toolpad/core/react-router-dom';

import '@fontsource/inter'; // Подключает шрифт Inter с весом по умолчанию (400)
import { RiHome9Fill } from 'react-icons/ri';


import { PiGearBold } from 'react-icons/pi';

// Удаляем импорт логотипа, так как используем файл из папки public

import { injectStores } from '@mobx-devtools/tools';
import sessionStore from './stores/sessionStore';
import shopStore from './stores/shopStore';
import partnershipStore from './stores/partnershipStore';
import { observer } from 'mobx-react-lite';

import { createTheme, CssBaseline, ThemeProvider, CircularProgress } from '@mui/material';


import logger from './utils/logger';
import { auth } from './firebaseConfig';
import ShopRegistration from './components/ShopRegistration';

/* import { fixStaticResourcePath } from './utils/imageUtils' */

// Отладочная информация для переменных окружения
logger.log('🔧 Environment variables check:');
logger.log(`VITE_APP_MODE: "${import.meta.env.VITE_APP_MODE}"`);
logger.log(`NODE_ENV: ${import.meta.env.NODE_ENV}`);
logger.log(`MODE: ${import.meta.env.MODE}`);
logger.log('🔧 Все VITE переменные:', import.meta.env);

// Инъекция хранилищ только в режиме разработки
// Проверяем несколько условий для определения режима разработки
const isDevelopment = import.meta.env.VITE_APP_MODE === 'development' || 
                     import.meta.env.MODE === 'development' || 
                     import.meta.env.DEV;

if (isDevelopment) {
  logger.log('MobX DevTools: подключаем хранилища в режиме разработки');
  injectStores({
    sessionStore,
    shopStore,
    partnershipStore,
  });
}

const App = observer(() => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const [showShopForm, setShowShopForm] = useState(false);
  const navigate = useNavigate();

  // Шаг 1: Проверка авторизации отдельно от загрузки данных
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Ждем, пока Firebase проверит состояние авторизации
        await new Promise((resolve) => {
          const unsubscribe = auth.onAuthStateChanged((user) => {
            logger.log(`👤 Firebase Auth State Changed: ${user ? 'User exists' : 'No user'}`);
            unsubscribe();
            resolve(user);
          });
        });

        // Попытка восстановить сессию
        await sessionStore.restoreSession();

        // Отмечаем, что проверка авторизации завершена
        setAuthCheckComplete(true);
      } catch (error) {
        logger.error('Ошибка проверки авторизации:', error);
        setAuthCheckComplete(true); // Всё равно завершаем проверку авторизации
      }
    };

    checkAuth();
  }, []);

  // Шаг 2: Инициализация приложения только после проверки авторизации
  useEffect(() => {
    // Не начинаем загрузку данных, пока не проверим авторизацию
    if (!authCheckComplete) return;

    /* 		debugStore.showComponentName = true
		debugStore.showGetMeButton = true */

    const initializeApp = async () => {
      setIsLoading(true);
      try {
        // Инициализация, не требующая авторизации

        // Если пользователь не авторизован, просто завершаем инициализацию
        if (!sessionStore.user) {
          logger.log('👨‍🦱 Пользователь не авторизован, перенаправляем на страницу входа');
          setIsInitialized(true);
          return;
        }

        logger.log(`👨‍🦱 Авторизованный пользователь: ${JSON.stringify(sessionStore.user)}`);

        // Проверяем наличие магазина
        try {
          const hasShop = await shopStore.checkShop();
          if (!hasShop) {
            logger.log('🏪 У пользователя нет зарегистрированного магазина, показываем форму');
            setShowShopForm(true);
          } else {
            logger.log('🏪 Магазин найден:', shopStore.shop);
          }
        } catch (error) {
          logger.error('Ошибка при проверке магазина:', error);
        }

        setIsInitialized(true);
      } catch (error) {
        logger.error('Ошибка инициализации приложения:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
  }, [authCheckComplete]);

  // Перенаправление на страницу логина, если пользователь не авторизован
  useEffect(() => {
    if (authCheckComplete && !isLoading && !sessionStore.user) {
      // Получаем текущий путь
      const currentPath = window.location.pathname;

      // Разрешенные пути для неавторизованных пользователей
      const allowedPaths = ['/login', '/register', '/forgot', '/share'];

      // Проверяем, является ли текущий путь разрешенным
      const isAllowedPath = allowedPaths.some((path) => currentPath.startsWith(path));

      // Если путь не разрешен, перенаправляем на страницу логина
      if (!isAllowedPath) {
        logger.log(`Неавторизованный доступ к ${currentPath}, перенаправление на /login`);
        navigate('/login', { replace: true });
      }
    }
  }, [authCheckComplete, isLoading, sessionStore.user, navigate]);

  // Автоматическое обновление session при изменении sessionStore.user
  useEffect(() => {
    if (sessionStore.user) {
      setSession({
        user: {
          email: sessionStore.user.email || localStorage.getItem('userEmail'),
          image: '/assets/default-avatar.png',
        },
      });
    } else {
      setSession(null);
    }
  }, [sessionStore.user]);

  useEffect(() => {
    const handleStorageChange = () => {
      // Если sessionStore.user есть, приоритет у него
      if (!sessionStore.user) {
        setSession({
          user: {
            email: localStorage.getItem('userEmail'),
            image: '/assets/default-avatar.png',
          },
        });
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [sessionStore.user]);

  const [session, setSession] = useState(null);

  const updateSession = () => {
    setSession({
      user: {
        email: localStorage.getItem('userEmail'),
        image: '/assets/default-avatar.png',
      },
    });
  };

  const authentication = useMemo(() => {
    return {
      signIn: async () => {
        // Восстанавливаем сессию, session обновится автоматически через useEffect
        await sessionStore.restoreSession();
      },
      signOut: async () => {
        try {
          logger.log('🚪 Начинаем процесс выхода из системы');

          // Сбрасываем состояние приложения
          setIsInitialized(false);
          setShowShopForm(false);

          // Выполняем logout через sessionStore
          // session обновится автоматически через useEffect когда sessionStore.user станет null
          await sessionStore.logout();

          // Принудительно перенаправляем на страницу логина
          navigate('/login', { replace: true });

          logger.log('🚪 Выход из системы завершен');
        } catch (error) {
          logger.error('Ошибка при выходе из системы:', error);
          // В любом случае пытаемся перенаправить на логин
          navigate('/login', { replace: true });
        }
      },
    };
  }, [navigate]);

  // Отображаем "Загрузка..." до инициализации приложения
  if (!authCheckComplete || isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}>
        <CircularProgress />
        <div style={{ marginLeft: '10px' }}>
          {!authCheckComplete ? 'Проверка авторизации...' : 'Загрузка приложения...'}
        </div>
      </div>
    );
  }

  // Убираем блокирующую проверку - если пользователь авторизован, показываем интерфейс
  // Инициализация данных может происходить в фоне

  const NAVIGATION = [
    {
      title: 'Главная',
      icon: <RiHome9Fill size={20} />,
    },
    {
      segment: `options`,
      title: 'Настройки',
      icon: <PiGearBold size={20} color="gray" />,
    },
  ];

  const customTheme = createTheme({
    typography: {
      fontFamily: '"Inter", sans-serif',
    },
    // Если требуется, можно задать styleOverrides для MuiTypography:
    components: {
      MuiTypography: {
        styleOverrides: {
          root: {
            fontFamily: '"Inter", sans-serif',
          },
        },
      },
    },
  });

  const handleShopSuccess = () => {
    setShowShopForm(false);
    logger.log('🏪 Магазин успешно зарегистрирован, скрываем форму');
  };

  return (
    <ThemeProvider theme={customTheme}>
      <CssBaseline />
      <AppProvider
        navigation={NAVIGATION}
        session={session}
        authentication={authentication}
        branding={{
          logo: <img src="/logo.png" alt="Logo" />,
          title: 'Calc5',
        }}>
        <Outlet />
        {/* {sessionStore.user && <UserSettingsModal />} */}

        {/* Форма регистрации магазина */}
        <ShopRegistration
          open={showShopForm}
          onSuccess={handleShopSuccess}
          onClose={() => setShowShopForm(false)}
        />
      </AppProvider>
    </ThemeProvider>
  );
});

export default App;
